import React, { useEffect, useMemo, useState } from "react";
import {
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Line,
} from "recharts";
import dayjs from "dayjs";

import TempIcon from "../../../../../Assets/vitals/Temperature.svg";
import LiverIcon from "../../../../../Assets/vitals/Oxygen.svg";
import HeartIcon from "../../../../../Assets/vitals/Heart-Rate.svg";
import EcgIcon from "../../../../../Assets/vitals/respiration.svg";
import WeightIcon from "../../../../../Assets/vitals/Weight.svg";
import bmi from "../../../../../Assets/Images/bmi.png";
import BloodPressureIcon from "../../../../../Assets/vitals/Blood-Pressure.svg";
import VitalService from "../../../../../Services/VitalService";

const VITAL_COLUMN_DETAILS = {
  BP: {
    label: "Blood Pressure",
    keys: ["BPS", "BPD"],
    icon: BloodPressureIcon,
  },
  RR: { label: "Respiratory Rate", keys: ["RR"], icon: EcgIcon },
  TP: { label: "Temperature", keys: ["TP"], icon: TempIcon },
  HR: { label: "Heart Rate", keys: ["HR"], icon: HeartIcon },
  SPO2: { label: "SpO2", keys: ["SPO2"], icon: LiverIcon },
  WE: { label: "Weight", keys: ["WE"], icon: WeightIcon },
  // HI: { label: "Height", keys: ["HI"], icon: WeightIcon },
  BMI: { label: "BMI", keys: ["BMI"], icon: bmi },
};

function VitalTrends({
  rpmConfigVitalsList,
  vitalDisplayMap,
  selectedRpmPatient,
}) {
  const [mode, setMode] = useState("7");
  const [selectedVital, setSelectedVital] = useState(null);
  const [displayData, setDisplayData] = useState([]);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);

  const configuredVitals = useMemo(() => {
    if (!rpmConfigVitalsList) return [];

    let configured = Object.entries(rpmConfigVitalsList)
      .filter(([key, val]) => key !== "status" && val === 1)
      .map(([key]) => vitalDisplayMap[key])
      .filter((v) => !!v); // remove undefined mappings

    const hasBPS = configured.includes("BPS");
    const hasBPD = configured.includes("BPD");

    if (hasBPS || hasBPD) {
      configured = configured.filter((v) => v !== "BPS" && v !== "BPD");
      if (!configured.includes("BP")) configured.unshift("BP");
    }

    return [...new Set(configured)].filter(
      (code) => VITAL_COLUMN_DETAILS[code],
    );
  }, [rpmConfigVitalsList, vitalDisplayMap]);

  useEffect(() => {
    if (configuredVitals.length > 0 && !selectedVital) {
      setSelectedVital(configuredVitals[0]);
    }
  }, [configuredVitals]);

  const fetchVitalData = async () => {
    if (!selectedRpmPatient || !selectedVital) return;

    setLoading(true);
    try {
      const keys = VITAL_COLUMN_DETAILS[selectedVital].keys;

      const fromDate =
        mode === "1"
          ? dayjs(currentDate).startOf("day")
          : dayjs(currentDate).subtract(6, "day").startOf("day");

      const toDate = dayjs(currentDate).endOf("day");

      const payload = {
        patientId: selectedRpmPatient.patientId,
        vitalCodes: keys,
        fromDate: fromDate.format("YYYY-MM-DD HH:mm:ss"),
        toDate: toDate.format("YYYY-MM-DD HH:mm:ss"),
      };

      const res = await VitalService.getAllVitalsData(payload);
      const items = res?.data || [];

      const grouped = {};
      items.forEach((x) => {
        const timeKey = x.recordedDateTime;
        if (!grouped[timeKey]) {
          grouped[timeKey] = {
            fullDateTime: x.recordedDateTime,
            timestamp: dayjs(x.recordedDateTime).valueOf(),
            date: dayjs(x.recordedDateTime).format("DD/MM/YYYY"),
          };
        }
        grouped[timeKey][x.vitalCode] = x.value ?? null;
      });

      const formattedData = Object.values(grouped);

      let finalData = formattedData;

      if (mode === "7") {
        const latestPerDay = {};
        formattedData.forEach((v) => {
          const d = v.date;
          if (!latestPerDay[d]) {
            latestPerDay[d] = {
              date: d,
              fullDateTime: v.fullDateTime,
              timestamp: v.timestamp,
            };
          }
          
          keys.forEach((k) => {
            if (v[k] !== undefined && v[k] !== null) {
              if (
                latestPerDay[d][k] === undefined ||
                dayjs(v.fullDateTime).isAfter(latestPerDay[d].fullDateTime)
              ) {
                latestPerDay[d][k] = v[k];
              }
            }
          });

          if (dayjs(v.fullDateTime).isAfter(latestPerDay[d].fullDateTime)) {
            latestPerDay[d].fullDateTime = v.fullDateTime;
            latestPerDay[d].timestamp = v.timestamp;
          }
        });
        finalData = Object.values(latestPerDay);
      }

      finalData.sort((a, b) =>
        dayjs(a.fullDateTime).isAfter(b.fullDateTime) ? 1 : -1,
      );

      setDisplayData(finalData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVitalData();
  }, [selectedVital, mode, currentDate]);

  const handlePrev = () => {
    setCurrentDate((prev) => dayjs(prev).subtract(mode === "1" ? 1 : 7, "day"));
  };

  const handleNext = () => {
    if (dayjs(currentDate).isSame(dayjs(), "day")) return;
    setCurrentDate((prev) => dayjs(prev).add(mode === "1" ? 1 : 7, "day"));
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-800">Health Trends</h2>

      {/* Vital Icon Selection */}
      <div className="flex gap-3 justify-center my-4">
        {configuredVitals.map((code) => (
          <img
            key={code}
            src={VITAL_COLUMN_DETAILS[code]?.icon}
            alt={code}
            className={`w-8 h-8 cursor-pointer ${
              selectedVital === code ? "scale-110" : ""
            }`}
            onClick={() => setSelectedVital(code)}
          />
        ))}
      </div>

      {/* Day Mode Buttons */}
      <div className="flex gap-3 justify-center my-3">
        <button
          className={`${
            mode === "1"
              ? "bg-brand-primaryColor text-white"
              : "bg-white border"
          } px-4 py-2 rounded`}
          onClick={() => setMode("1")}
        >
          1D
        </button>
        <button
          className={`${
            mode === "7"
              ? "bg-brand-primaryColor text-white"
              : "bg-white border"
          } px-4 py-2 rounded`}
          onClick={() => setMode("7")}
        >
          7D
        </button>
      </div>

      {/* Date Navigation */}
      <div className="flex justify-between mb-3">
        <button className="px-3 py-2 rounded bg-gray-300" onClick={handlePrev}>
          ← Prev
        </button>

        <span>
          {mode === "1"
            ? currentDate.format("DD/MM/YYYY")
            : `${dayjs(currentDate)
                .subtract(6, "day")
                .format("DD/MM/YYYY")} - ${currentDate.format("DD/MM/YYYY")}`}
        </span>

        <button
          className="px-3 py-2 rounded bg-gray-300"
          onClick={handleNext}
          disabled={dayjs(currentDate).isSame(dayjs(), "day")}
        >
          Next →
        </button>
      </div>

      <h3 className="text-lg font-semibold text-center mt-4 mb-2">
        {VITAL_COLUMN_DETAILS[selectedVital]?.label}
      </h3>

      {/* Chart or Loader / No Data */}
      {loading ? (
        <div className="flex justify-center items-center h-72 text-gray-500">
          Loading...
        </div>
      ) : displayData.length === 0 ? (
        <div className="flex justify-center items-center h-72 text-gray-500">
          No data available
        </div>
      ) : (
        <div className="w-full h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={displayData}>
              {mode === "7" ? (
                <XAxis dataKey="date" />
              ) : (
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={[
                    dayjs(currentDate).startOf("day").valueOf(),
                    dayjs(currentDate).endOf("day").valueOf(),
                  ]}
                  tickFormatter={(unixTime) => dayjs(unixTime).format("hh A")}
                  ticks={[
                    dayjs(currentDate).startOf("day").valueOf(),
                    dayjs(currentDate).startOf("day").add(3, "hour").valueOf(),
                    dayjs(currentDate).startOf("day").add(6, "hour").valueOf(),
                    dayjs(currentDate).startOf("day").add(9, "hour").valueOf(),
                    dayjs(currentDate).startOf("day").add(12, "hour").valueOf(),
                    dayjs(currentDate).startOf("day").add(15, "hour").valueOf(),
                    dayjs(currentDate).startOf("day").add(18, "hour").valueOf(),
                    dayjs(currentDate).startOf("day").add(21, "hour").valueOf(),
                    dayjs(currentDate).endOf("day").valueOf(),
                  ]}
                />
              )}
              <YAxis allowDecimals={false} />
              <Tooltip
                formatter={(value, name, props) => {
                  if (name === "BPS") return [value, "Systolic (BPS)"];
                  if (name === "BPD") return [value, "Diastolic (BPD)"];
                  const selected = VITAL_COLUMN_DETAILS[selectedVital];
                  return [value, selected?.label || name];
                }}
                labelFormatter={(label, items) => {
                  const payload = items?.[0]?.payload;
                  if (!payload?.fullDateTime) return "";
                  return dayjs(payload.fullDateTime).format(
                    "DD/MM/YYYY hh:mm A",
                  );
                }}
              />

              {VITAL_COLUMN_DETAILS[selectedVital]?.keys?.map((key, i) => (
                <Line
                  key={key}
                  dataKey={key}
                  stroke={i === 0 ? "#5B8FF9" : "#5AD8A6"}
                  dot
                  connectNulls
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default VitalTrends;
